/**
 * Error Processors Registry
 *
 * This module provides a registry for custom error processors.
 * Projects can register processors for specific error codes to customize
 * how error details are rendered.
 *
 * Usage in project-specific code:
 *
 *   import { errorProcessors } from "../api/errorProcessors";
 *
 *   errorProcessors.register("MUSICAL_WORK_MISSING_SCORE", async (meta, context) => {
 *     // Custom processing - can fetch additional data using item IDs
 *     const enrichedItems = await Promise.all(
 *       meta.items.map(async (item) => {
 *         const work = await fetchMusicalWork(item.id);
 *         return `${item.label} (${work.composer})`;
 *       })
 *     );
 *     return enrichedItems.map(text => `• ${text}`).join("\n");
 *   });
 */

import { LangStore } from "../store/LangStore";
import {
  StructuredErrorMeta,
  ErrorItem,
  resolveText,
  isStructuredErrorMeta,
} from "./errorTypes";
import {
  DeletionBlockers,
  deletionBlockersText,
} from "../common/deletion/deletionBlockersText";

// Context passed to processors for accessing stores and services
export interface ErrorProcessorContext {
  langStore: LangStore;
  language: "es" | "en" | "pt";
  // Projects can extend this interface with additional services
  [key: string]: unknown;
}

// Processor function signature
// Returns the formatted message string (can be async for data fetching)
export type ErrorProcessor = (
  meta: StructuredErrorMeta,
  context: ErrorProcessorContext
) => string | Promise<string>;

// Registry for custom processors
class ErrorProcessorRegistry {
  private processors = new Map<string, ErrorProcessor>();

  /**
   * Register a custom processor for a specific error code
   */
  register(code: string, processor: ErrorProcessor): void {
    this.processors.set(code, processor);
  }

  /**
   * Unregister a processor
   */
  unregister(code: string): void {
    this.processors.delete(code);
  }

  /**
   * Check if a custom processor exists for a code
   */
  has(code: string): boolean {
    return this.processors.has(code);
  }

  /**
   * Get a custom processor
   */
  get(code: string): ErrorProcessor | undefined {
    return this.processors.get(code);
  }

  /**
   * Process error using standard renderer (synchronous)
   * For custom async processors, use processAsync() instead
   */
  process(
    code: string,
    meta: StructuredErrorMeta | Record<string, unknown>,
    context: ErrorProcessorContext
  ): string {
    const customProcessor = this.processors.get(code);
    if (customProcessor && isStructuredErrorMeta(meta)) {
      const result = customProcessor(meta, context);
      if (typeof result === "string") return result;
    }
    return this.standardRender(meta, context);
  }

  /**
   * Process error using custom processor or standard renderer (async)
   * Use this when you need custom processor support
   */
  async processAsync(
    code: string,
    meta: StructuredErrorMeta | Record<string, unknown>,
    context: ErrorProcessorContext
  ): Promise<string> {
    // Try custom processor first
    const customProcessor = this.processors.get(code);
    if (customProcessor && isStructuredErrorMeta(meta)) {
      return await customProcessor(meta, context);
    }

    // Fall back to standard rendering
    return this.standardRender(meta, context);
  }

  /**
   * Standard rendering based on detailType
   */
  private standardRender(
    meta: StructuredErrorMeta | Record<string, unknown>,
    context: ErrorProcessorContext
  ): string {
    const { language } = context;

    if (!isStructuredErrorMeta(meta)) {
      // Legacy format - try to extract message
      return this.renderLegacyMeta(meta, context);
    }

    switch (meta.detailType) {
      case "item-list":
        return this.renderItemList(meta.items || [], language);

      case "field-errors":
        return this.renderFieldErrors(meta.fieldErrors || [], language);

      case "key-value":
        return this.renderKeyValue(meta.entries || [], language);

      case "message":
        return resolveText(meta.message, language);

      case "custom":
        // Custom type without registered processor - show raw or message
        if (meta.message) {
          return resolveText(meta.message, language);
        }
        return JSON.stringify(meta.raw || {});

      default:
        return "";
    }
  }

  /**
   * Render item-list type
   */
  private renderItemList(
    items: ErrorItem[],
    language: "es" | "en" | "pt"
  ): string {
    return items
      .map((item) => `• ${resolveText(item.label, language)}`)
      .join("\n");
  }

  /**
   * Render field-errors type
   */
  private renderFieldErrors(
    errors: Array<{
      field: string;
      message: string | { es: string; en: string; pt?: string };
    }>,
    language: "es" | "en" | "pt"
  ): string {
    return errors
      .map((err) => `• ${err.field}: ${resolveText(err.message, language)}`)
      .join("\n");
  }

  /**
   * Render key-value type
   */
  private renderKeyValue(
    entries: Array<{
      key: string | { es: string; en: string; pt?: string };
      value: string | { es: string; en: string; pt?: string };
    }>,
    language: "es" | "en" | "pt"
  ): string {
    return entries
      .map(
        (entry) =>
          `${resolveText(entry.key, language)}: ${resolveText(entry.value, language)}`
      )
      .join("\n");
  }

  /**
   * Handle legacy meta format (without detailType)
   */
  private renderLegacyMeta(
    meta: Record<string, unknown>,
    context: ErrorProcessorContext
  ): string {
    const { language } = context;

    // Try validationErrors array (legacy format)
    if (
      Array.isArray(meta.validationErrors) &&
      meta.validationErrors.length > 0
    ) {
      return meta.validationErrors
        .map((err: Record<string, unknown>) => {
          // Try common label fields
          const label =
            err.label ||
            err.workTitle ||
            err.itemTitle ||
            err.title ||
            err.name ||
            err.field ||
            "";
          const text =
            typeof label === "object"
              ? resolveText(
                  label as { es: string; en: string; pt?: string },
                  language
                )
              : String(label);
          return text ? `• ${text}` : null;
        })
        .filter(Boolean)
        .join("\n");
    }

    // Try message field
    if (meta.message) {
      return typeof meta.message === "object"
        ? resolveText(
            meta.message as { es: string; en: string; pt?: string },
            language
          )
        : String(meta.message);
    }

    return "";
  }
}

// Singleton instance
export const errorProcessors = new ErrorProcessorRegistry();

// ENTITY_IN_USE: el server rechazó un borrado porque el registro está en uso
// (lo verificó al borrar, o la base lo rechazó y PrismaError lo tradujo).
// Manda los datos en meta.blockers, sin texto: el texto se arma acá, en el
// idioma del usuario, igual que en el diálogo de confirmación de las listas.
errorProcessors.register("ENTITY_IN_USE", (meta, context) => {
  const blockers = meta.blockers as DeletionBlockers | undefined;
  if (!blockers || typeof blockers !== "object") return "";
  // Nunca tira: si tirara, getApiError caería a su catch final y el formulario
  // mostraría el error crudo (ids, variables de la mutación).
  try {
    return deletionBlockersText(blockers, context.langStore).lines.join("\n");
  } catch {
    return "";
  }
});
